import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { Button, LinearProgress, Menu, MenuItem, TextField } from '@mui/material';
import {
	DataGrid,
	GridCsvExportMenuItem,
	GridExportMenuItemProps,
	GridPrintExportMenuItem,
	GridToolbarExportContainer,
	GridValidRowModel,
} from '@mui/x-data-grid';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { API } from '~/api';
import { AppRoutes } from '~/app/app.routes';
import { AuthService } from '~/service/service.auth';
import { BrandService } from '~/service/service.brand';
import { FragranceService } from '~/service/service.fragrance';
import { NoteService } from '~/service/service.note';
import { OlfactiveService } from '~/service/service.olfactive';
import { PerfumerService } from '~/service/service.perfumer';
import { UIService } from '~/service/service.ui';
import { FragranceModerationStatus } from '~/shared/dto/model.enums';
import { FTSStrategy } from '~/shared/dto/paging.dto';
import { Enum } from '~/shared/fields';
import { useService } from '~/shared/service/service.base';
import { FragranceModerationDialog } from '~/view/dialog/dialog.fragrance.moderation';
import { FragrancesFTSDialog } from '~/view/dialog/dialog.fragrances.fts';
import { FragrancesImpressionsDialog } from '~/view/dialog/dialog.fragrances.impressions';
import { ImportFragranceDialog, ImportFragrancePhotosDialog } from '~/view/dialog/dialog.import';
import { FieldAutocompleteSingle } from '~/view/fields/field.autocomplete';
import {
	ActionsColumn,
	ArrayColumn,
	AvatarColumn,
	CheckedColumn,
	DateTimeColumn,
	EnumColumn,
	GridColumn,
	LinkColumn,
	useGridColumns,
} from '~/view/grid/grid.columns';
import { GridContainer } from '~/view/grid/grid.container';
import { GridNoRows } from '~/view/grid/grid.norows';
import { MuiGridPagination } from '~/view/grid/grid.pagination';
import { GridToolbarView } from '~/view/grid/grid.toolbar';
import { Row } from '~/view/view.box';
import { PerformTimeView } from '~/view/view.perform.time';

export const FragranceListPage = observer(() => {
	const [ui, fragrances, brands, olfactives, perfumers, notes] = useService(
		UIService,
		FragranceService,
		BrandService,
		OlfactiveService,
		PerfumerService,
		NoteService,
	);

	const columns = useGridColumns<API.FragranceItem>(() => [
		AvatarColumn({ field: 'photo', headerName: 'Photo', tooltip: row => row.relevance }),
		CheckedColumn({ field: 'active', headerName: 'Active' }),
		CheckedColumn({ field: 'discontinued', headerName: 'Discontinued' }),
		{ field: 'id', headerName: 'ID' },
		ModerationColumn({
			field: 'moderationStatus',
			headerName: 'Moderation',
			onClick: row => fragrances.moderation.showDialog(row.id, fragrances.reload),
		}),
		{ field: 'name', headerName: 'Name', flex: 1, minWidth: 200 },
		{ field: 'brandName', headerName: 'Brand', minWidth: 150 },
		DateTimeColumn({ field: 'photoUpdateAt', headerName: 'Photo updated at' }),
		{ field: 'asinActiveCount', headerName: 'ASIN' },
		EnumColumn('Longevity', { field: 'longevity', headerName: 'Longevity', width: 100 }),
		ArrayColumn({ field: 'olfactiveNames', headerName: 'Olfactive', width: 150 }),
		EnumColumn('Sillage', { field: 'sillage', headerName: 'Sillage', minWidth: 100 }),
		ArrayColumn({ field: 'years', headerName: 'Years' }),
		ArrayColumn({ field: 'perfumerNames', headerName: 'Perfumers', minWidth: 100 }),
		{ field: 'description', headerName: 'Description', flex: 1, minWidth: 200 },
		LinkColumn({
			field: 'superId',
			headerName: 'Super',
			getLink: row => AppRoutes.FragranceEdit.buildPath({ id: row.superId ?? 0 }),
		}),
		ActionsColumn({
			editRoute: AppRoutes.FragranceEdit.buildPath,
			onDelete: fragrances.delete,
			permission: 'fragrance',
		}),
	]);

	ui.usePageTitle('Fragrances');
	useEffect(() => fragrances.loadAndSubscribe(), [fragrances]);

	useEffect(() => {
		brands.fetchNames();
		olfactives.fetchNames();
		perfumers.fetchNames();
		notes.fetchNames();
	}, [brands, olfactives, perfumers, notes]);

	const toolbar = useCallback(() => {
		return <FragranceListFilterFields />;
	}, []);

	return (
		<GridContainer async={fragrances.async}>
			<DataGrid
				initialState={fragrances.gridState.initial()}
				loading={fragrances.async.loading}
				rowCount={fragrances.totalCount}
				rows={fragrances.items.slice()}
				columns={columns}
				density={fragrances.gridState.density}
				paginationMode="server"
				disableColumnFilter
				sortingMode="server"
				onStateChange={fragrances.gridState.onStateChange}
				onFilterModelChange={fragrances.changeFilterModel}
				onPaginationModelChange={fragrances.gridState.onPaginationModelChange}
				paginationModel={fragrances.gridState.paginationModel}
				slots={{
					loadingOverlay: LinearProgress,
					noRowsOverlay: () => <GridNoRows>No fragrances found</GridNoRows>,
					pagination: MuiGridPagination,
					toolbar,
				}}
			/>
			<PerformTimeView time={fragrances.searchTime} />
			<ImportFragranceDialog />
			<ImportFragrancePhotosDialog />
			<FragranceModerationDialog />
		</GridContainer>
	);
});

const RightMenu = observer(() => {
	const [ui, auth] = useService(UIService, AuthService);
	const btnRecalcRef = useRef<HTMLButtonElement>(null);
	const [menuRecalcOpen, menuRecalcToggle] = useReducer(v => !v, false);

	const handleClickImpressions = useCallback(() => {
		menuRecalcToggle();
		ui.showDialog(FragrancesImpressionsDialog);
	}, [ui]);

	const handleClickFTS = useCallback(() => {
		menuRecalcToggle();
		ui.showDialog(FragrancesFTSDialog);
	}, [ui]);

	return (
		<Row>
			{auth.can('fragrance', 'UPDATE') ? (
				<>
					<Button ref={btnRecalcRef} size="small" startIcon={<RestartAltIcon />} onClick={menuRecalcToggle}>
						Recalculate
					</Button>
					<Menu open={menuRecalcOpen} anchorEl={btnRecalcRef.current} onClose={menuRecalcToggle}>
						<MenuItem onClick={handleClickImpressions}>Impressions</MenuItem>
						<MenuItem onClick={handleClickFTS}>FTS Tokens</MenuItem>
					</Menu>
					<FragrancesImpressionsDialog />
					<FragrancesFTSDialog />
				</>
			) : null}
			{auth.can('fragrance', 'EXPORT') ? <GridToolbarExport /> : null}
		</Row>
	);
});

export function ModerationColumn<T extends GridValidRowModel>(
	def: GridColumn<T> & { onClick: (row: T) => void },
): GridColumn<T> {
	return {
		minWidth: 120,
		filterable: false,
		sortable: false,
		renderCell: ({ value, row }) => (
			<Button variant="text" color="inherit" sx={{ fontWeight: 400 }} onClick={() => def.onClick(row)}>
				{value || 'NONE'}
			</Button>
		),
		...def,
	};
}

function ImagesExportMenuItem(props: GridExportMenuItemProps<{}>) {
	const [fragrances] = useService(FragranceService);
	const { hideMenu } = props;

	return (
		<MenuItem
			onClick={() => {
				fragrances.exportImages();
				hideMenu?.();
			}}
		>
			Export Images
		</MenuItem>
	);
}

function ImportCSVMenuItem(props: GridExportMenuItemProps<{}>) {
	const [ui] = useService(UIService);
	const { hideMenu } = props;

	return (
		<MenuItem
			onClick={() => {
				hideMenu?.();
				ui.showDialog(ImportFragranceDialog);
			}}
		>
			Import from CSV
		</MenuItem>
	);
}

function ImportPhotosCSVMenuItem(props: GridExportMenuItemProps<{}>) {
	const [ui] = useService(UIService);
	const { hideMenu } = props;

	return (
		<MenuItem
			onClick={() => {
				hideMenu?.();
				ui.showDialog(ImportFragrancePhotosDialog);
			}}
		>
			Import photos from CSV
		</MenuItem>
	);
}

function ExportCSVMenuItem(props: GridExportMenuItemProps<{}>) {
	const [fragSvc, authSvc] = useService(FragranceService, AuthService);
	const { hideMenu } = props;

	return authSvc.can('fragrance', 'EXPORT') ? (
		<MenuItem
			onClick={() => {
				hideMenu?.();
				fragSvc.exportCSV();
			}}
		>
			Export to CSV
		</MenuItem>
	) : null;
}

function GridToolbarExport({ csvOptions, printOptions, ...other }: any) {
	return (
		<GridToolbarExportContainer {...other}>
			<GridCsvExportMenuItem options={csvOptions} />
			<GridPrintExportMenuItem options={printOptions} />
			<ImagesExportMenuItem />
			<ExportCSVMenuItem />
			<ImportCSVMenuItem />
			<ImportPhotosCSVMenuItem />
		</GridToolbarExportContainer>
	);
}

export const FragranceListFilterFields = observer(() => {
	const [fragrances, brands, auth] = useService(FragranceService, BrandService, AuthService);

	useEffect(() => brands.fetchNames(), [brands]);

	const activeItems = useMemo<{ value: 0 | 1; label: string }[]>(() => {
		return [
			{ value: 1, label: 'ACTIVE' },
			{ value: 0, label: 'INACTIVE' },
		];
	}, []);

	return (
		<GridToolbarView
			onReload={fragrances.reload}
			gridState={fragrances.gridState}
			onCreateUrl={auth.can('fragrance', 'CREATE') ? AppRoutes.FragranceNew.buildPath({}) : undefined}
			exportEnabled={false}
			rightMenu={<RightMenu />}
		>
			<TextField
				select
				label="Strategy"
				fullWidth
				value={fragrances.searchStrategy}
				onChange={fragrances.changeSearchStrategy}
			>
				{Enum.keys(FTSStrategy).map(s => (
					<MenuItem key={s} value={s}>
						{s}
					</MenuItem>
				))}
			</TextField>
			<FieldAutocompleteSingle
				label="Active"
				field={fragrances.filterFieldsForm.$active}
				items={activeItems}
				valueProp="value"
				labelProp="label"
				sx={{ minWidth: 150 }}
			/>
			<FieldAutocompleteSingle
				label="Moderation"
				field={fragrances.filterFieldsForm.$moderationStatus}
				items={Enum.keys(FragranceModerationStatus).map(value => ({ value, label: value }))}
				valueProp="value"
				labelProp="label"
				sx={{ minWidth: 150 }}
			/>
			<FieldAutocompleteSingle
				items={brands.names.slice()}
				valueProp="id"
				labelProp="name"
				enabledProp="active"
				field={fragrances.filterFieldsForm.$brandId}
				sx={{ minWidth: 250 }}
			/>
		</GridToolbarView>
	);
});
